import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import { Icons } from '../components/shared/Icons'
import { useAuth } from '../context/AuthContext'
import './DraftPages.css'

export default function SignIn() {
  const navigate = useNavigate()
  const { login, loading: authLoading, error: authError, clearError } = useAuth()

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [status, setStatus] = useState('idle') // idle, submitting, success, error
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
    if (error) setError('')
    if (authError) clearError()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setError('')

    // Basic validation
    if (!formData.email || !formData.password) {
      setError('Please enter your email and password')
      setStatus('error')
      return
    }

    const result = await login(formData.email, formData.password)

    if (result.success) {
      setStatus('success')
      // Redirect to home or dashboard
      navigate('/')
    } else {
      setStatus('error')
      setError(result.error || 'Invalid email or password')
    }
  }

  return (
    <div className="draft-page">
      <section className="draft-hero demo-hero">
        <div className="container">
          <div className="demo-grid signin-grid">
            <div className="demo-content">
              <ScrollReveal>
                <span className="draft-badge">Welcome Back</span>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <h1 className="draft-title">
                  Sign In to{' '}
                  <GradientText gradient="solar">AI4Solar</GradientText>
                </h1>
              </ScrollReveal>
              <ScrollReveal delay={0.2}>
                <p className="draft-subtitle">
                  Access your solar monitoring dashboard and manage your fleet.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.3} className="demo-benefits">
                <div className="demo-benefit">
                  <span className="benefit-icon">
                    <Icons.activity size={24} />
                  </span>
                  <div>
                    <span className="benefit-title">Real-time monitoring</span>
                    <span className="benefit-desc">Track your solar assets 24/7</span>
                  </div>
                </div>
                <div className="demo-benefit">
                  <span className="benefit-icon">
                    <Icons.zap size={24} />
                  </span>
                  <div>
                    <span className="benefit-title">AI predictions</span>
                    <span className="benefit-desc">30-180 day failure forecasts</span>
                  </div>
                </div>
                <div className="demo-benefit">
                  <span className="benefit-icon">
                    <Icons.shield size={24} />
                  </span>
                  <div>
                    <span className="benefit-title">Secure access</span>
                    <span className="benefit-desc">Enterprise-grade security</span>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            <div className="demo-form-wrapper">
              <ScrollReveal delay={0.2}>
                <motion.div
                  className="demo-form-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <h2 className="form-title">Sign In</h2>

                  {status === 'success' ? (
                    <div className="form-success">
                      <div className="success-icon">
                        <Icons.check size={48} />
                      </div>
                      <h3>Welcome back!</h3>
                      <p>Redirecting you to your dashboard...</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="demo-form">
                      {(error || authError) && (
                        <div className="form-error-banner">
                          <Icons.alertCircle size={20} />
                          <span>{error || authError}</span>
                        </div>
                      )}

                      <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@company.com"
                          required
                          autoComplete="email"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <div className="password-input-wrapper">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter your password"
                            required
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <Icons.eyeOff size={20} /> : <Icons.eye size={20} />}
                          </button>
                        </div>
                      </div>

                      <div className="form-row form-row-between">
                        <label className="checkbox-label">
                          <input type="checkbox" name="remember" />
                          <span>Remember me</span>
                        </label>
                        <Link to="/forgot-password" className="form-link">
                          Forgot password?
                        </Link>
                      </div>

                      <GlowButton
                        variant="solar"
                        size="large"
                        className="form-submit-btn"
                        type="submit"
                        disabled={status === 'submitting' || authLoading}
                      >
                        {status === 'submitting' ? (
                          <>
                            <span className="loading-spinner" />
                            Signing in...
                          </>
                        ) : (
                          <>
                            Sign In
                            <Icons.arrowRight size={20} />
                          </>
                        )}
                      </GlowButton>

                      <p className="form-footer-text">
                        Don't have an account?{' '}
                        <Link to="/register" className="form-link">
                          Create account
                        </Link>
                      </p>
                    </form>
                  )}
                </motion.div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
